import { supabaseUrl, supabaseAnonKey, storeSession, clearSession } from '../supabase/config';
import { supabaseFetch } from '../utils/supabaseHelper';

// Timeout helper to prevent hanging operations
const withTimeout = (promise, ms, errorMsg = 'Operation timed out') => {
  const timeoutPromise = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(errorMsg)), ms)
  );
  return Promise.race([promise, timeoutPromise]);
};

/**
 * Sign in via Supabase REST API: POST /auth/v1/token?grant_type=password
 * Returns { user, session } on success.
 */
const signInWithPassword = async (email, password) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  try {
    const res = await fetch(`${supabaseUrl}/auth/v1/token?grant_type=password`, {
      method: 'POST',
      headers: {
        'apikey': supabaseAnonKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password }),
      signal: controller.signal
    });
    clearTimeout(timeoutId);

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.error_description || errBody.msg || errBody.message || 'Invalid credentials');
    }

    const data = await res.json();

    // Store session to localStorage
    storeSession({
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      expires_in: data.expires_in,
      token_type: data.token_type,
      user: data.user
    });

    return {
      data: {
        user: data.user,
        session: {
          access_token: data.access_token,
          refresh_token: data.refresh_token,
          expires_at: data.expires_at
        }
      },
      error: null
    };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === 'AbortError') {
      return { data: null, error: { message: 'Login timed out. Please try again.' } };
    }
    return { data: null, error: { message: err.message } };
  }
};

/**
 * Sign up via Supabase REST API: POST /auth/v1/signup
 */
const signUp = async (email, password, metadata = {}) => {
  try {
    const res = await fetch(`${supabaseUrl}/auth/v1/signup`, {
      method: 'POST',
      headers: {
        'apikey': supabaseAnonKey,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        email,
        password,
        data: metadata,
        emailRedirectTo: 'https://we-care-finserv.web.app/auth/confirm'
      })
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.msg || errBody.message || 'Signup failed');
    }

    const data = await res.json();

    // Store session if returned (email confirmation might be required)
    if (data.access_token) {
      storeSession({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
        expires_at: data.expires_at,
        user: data.user || data
      });
    }

    return {
      data: { user: data.user || data },
      error: null
    };
  } catch (err) {
    return { data: null, error: { message: err.message } };
  }
};

/**
 * Sign out via REST API + clear localStorage
 */
const signOutREST = async () => {
  try {
    // Try to call the logout endpoint (best-effort)
    const raw = window.localStorage.getItem('instacredmoney-auth');
    if (raw) {
      const stored = JSON.parse(raw);
      const token = stored.access_token || stored.session?.access_token;
      if (token) {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
          await fetch(`${supabaseUrl}/auth/v1/logout`, {
            method: 'POST',
            headers: {
              'apikey': supabaseAnonKey,
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
        } catch (e) {
          clearTimeout(timeoutId);
          // Ignore — clearing localStorage is what matters
        }
      }
    }
  } catch (e) {
    // Ignore
  }
  // Always clear local state regardless
  clearSession();
};

/**
 * Clear stale auth cache
 */
export const clearAuthCache = () => {
  clearSession();
  console.log('Auth cache cleared');
};

/**
 * Customer Signup
 */
export const customerSignup = async (email, password, name, phone) => {
  try {
    const { data: authData, error: authError } = await signUp(email, password, {
      name, phone, role: 'customer'
    });

    if (authError) throw new Error(authError.message);

    const user = authData.user;

    // Create user profile in users table via REST API
    // Note: If email confirmation is enabled, the session may not be valid yet.
    // We handle this gracefully — the profile will be created on first login if it fails here.
    try {
      await supabaseFetch('users', {
        method: 'POST',
        body: JSON.stringify([{
          id: user.id,
          email,
          name,
          phone,
          role: 'customer',
          status: 'active',
          created_at: new Date().toISOString(),
          last_login: new Date().toISOString()
        }]),
        headers: { 'Prefer': 'return=representation' }
      });
    } catch (profileErr) {
      console.warn('Could not save user profile during signup (email confirmation may be required):', profileErr.message);
    }

    return {
      success: true,
      user,
      message: 'Signup successful! Please check your email to verify your account.'
    };
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Customer Login
 */
export const customerLogin = async (email, password) => {
  try {
    console.log('Customer login: Attempting auth...');
    const { data: authData, error: authError } = await signInWithPassword(email, password);

    if (authError) {
      return { success: false, error: authError.message || 'Invalid credentials' };
    }

    if (!authData?.user) {
      return { success: false, error: 'Authentication failed - no user data' };
    }

    // Verify user is a customer
    console.log('Customer login: Fetching profile...');
    const accessToken = authData.session?.access_token;
    const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

    let userData;
    try {
      const users = await supabaseFetch(`users?id=eq.${authData.user.id}&select=*&limit=1`, { headers: authHeaders });
      if (users && users.length > 0) {
        userData = users[0];
      }
    } catch (dbException) {
      console.error('Customer login db exception:', dbException);
      await signOutREST();
      return { success: false, error: 'Database error while fetching user profile' };
    }

    if (!userData) {
      await signOutREST();
      return { success: false, error: 'User profile not found' };
    }

    if (userData.role !== 'customer') {
      await signOutREST();
      return { success: false, error: 'Invalid customer credentials' };
    }

    // Update last login (don't block)
    supabaseFetch(`users?id=eq.${authData.user.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ last_login: new Date().toISOString() })
    }).catch(err => console.warn('Failed to update last_login:', err));

    console.log('Customer login: Success');
    return { success: true, user: authData.user, userData };
  } catch (error) {
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      return { success: false, error: 'Request was cancelled. Please try again.' };
    }
    return { success: false, error: error.message || 'Login failed. Please try again.' };
  }
};

/**
 * Employee Login
 */
export const employeeLogin = async (email, password) => {
  try {
    console.log('Step 1: Attempting signInWithPassword via REST...');

    const { data: authData, error: authError } = await signInWithPassword(email, password);

    if (authError) {
      console.error('Step 1 Auth Error:', authError);
      return { success: false, error: authError.message || 'Invalid credentials' };
    }

    if (!authData?.user) {
      console.error('Step 1: No user returned from auth');
      return { success: false, error: 'Authentication failed - no user data' };
    }

    console.log('Step 1 Complete - Auth Result:', { userId: authData.user.id });

    // 2. Get user data
    console.log('Step 2: Fetching user profile from DB...');
    const accessToken = authData.session?.access_token;
    const authHeaders = accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

    let userData;
    let userTable = 'users';

    try {
      const users = await withTimeout(
        supabaseFetch(`users?id=eq.${authData.user.id}&select=*&limit=1`, { headers: authHeaders }),
        10000,
        'Database query timed out'
      ).catch(() => []);

      if (users && users.length > 0) {
        userData = users[0];
      }

      if (!userData) {
        console.log('User not found in users table, checking partners table...');
        const partners = await withTimeout(
          supabaseFetch(`partners?auth_user_id=eq.${authData.user.id}&select=*&limit=1`, { headers: authHeaders }),
          10000,
          'Database query timed out'
        ).catch(() => []);

        if (partners && partners.length > 0) {
          userData = partners[0];
          userTable = 'partners';
          if (!userData.role) userData.role = 'partner';
        }
      }

      console.log('Step 2 Complete - User Profile:', { hasData: !!userData, table: userTable });
    } catch (dbException) {
      console.error('Step 2 Exception:', dbException);
      await signOutREST();
      return { success: false, error: dbException.message || 'Database error while fetching user profile' };
    }

    if (!userData) {
      console.error('Step 2 Error: User profile not found');
      await signOutREST();
      return { success: false, error: 'User profile not found. Contact administrator.' };
    }

    // 3. Verify role
    console.log('Step 3: Verifying Role:', userData.role);
    if (!['employee', 'admin', 'partner'].includes(userData.role)) {
      console.warn('Step 3: Role verification failed');
      await signOutREST();
      return { success: false, error: 'Not authorized as employee or partner' };
    }

    // 4. Check status
    console.log('Step 4: Verifying Status:', userData.status);
    if (userData.status !== 'active') {
      console.warn('Step 4: Status check failed');
      await signOutREST();
      return { success: false, error: 'Account inactive. Contact administrator.' };
    }

    // 5. Update last login
    console.log('Step 5: Updating last_login...');
    if (userTable === 'users') {
      supabaseFetch(`users?id=eq.${authData.user.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ last_login: new Date().toISOString() })
      })
        .then(() => console.log('Step 5: last_login updated'))
        .catch(err => console.warn('Step 5: Failed to update last_login:', err));
    } else {
      console.log('Step 5: Skipping last_login for partner');
    }

    console.log('Login flow complete - returning success');
    return { success: true, user: authData.user, userData };
  } catch (error) {
    console.error('employeeLogin Exception:', error);
    if (error.name === 'AbortError' || error.message?.includes('aborted')) {
      return { success: false, error: 'Request was cancelled. Please try again.' };
    }
    return { success: false, error: error.message || 'Login failed. Please try again.' };
  }
};

/**
 * Logout
 */
export const logout = async () => {
  try {
    await signOutREST();
    return { success: true };
  } catch (error) {
    console.error('Logout error:', error);
    // Clear localStorage anyway
    clearSession();
    return { success: true };
  }
};

export default {
  clearAuthCache,
  customerSignup,
  customerLogin,
  employeeLogin,
  logout
};
